import { forwardRef, useRef, useState, useImperativeHandle, useEffect, type TextareaHTMLAttributes } from 'react';
import {
  type LucideIcon,
  Bold,
  Italic,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Code2,
  Link,
  Image,
  Table,
  Minus,
  Maximize2,
  Minimize2,
  Columns,
  Eye,
  PenTool,
} from 'lucide-react';
import { Markdown } from './Markdown';
import { MICROTASK_MS } from '@/shared/lib/constants';

interface MarkdownEditorProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  icon?: LucideIcon;
  error?: string;
  containerClassName?: string;
  hint?: string;
  isDisabled?: boolean;
}

export const MarkdownEditor = forwardRef<HTMLTextAreaElement, MarkdownEditorProps>(
  (
    { label, icon: Icon, error, hint, className = '', containerClassName = '', isDisabled, value, onChange, ...props },
    ref
  ) => {
    const [activeTab, setActiveTab] = useState<'write' | 'preview' | 'split'>('write');
    const [isFullScreen, setIsFullScreen] = useState(false);
    const localRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => localRef.current as HTMLTextAreaElement);

    // Handle full-screen body scroll lock
    useEffect(() => {
      if (isFullScreen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      return () => {
        document.body.style.overflow = '';
      };
    }, [isFullScreen]);

    const handleFormat = (type: string) => {
      const textarea = localRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const selectedText = text.substring(start, end);

      let replacement = '';
      let cursorOffsetStart = 0;
      let cursorOffsetEnd = 0;

      switch (type) {
        case 'bold':
          replacement = `**${selectedText || 'bold text'}**`;
          cursorOffsetStart = selectedText ? 0 : 2;
          cursorOffsetEnd = selectedText ? 0 : 2;
          break;
        case 'italic':
          replacement = `*${selectedText || 'italic text'}*`;
          cursorOffsetStart = selectedText ? 0 : 1;
          cursorOffsetEnd = selectedText ? 0 : 1;
          break;
        case 'strikethrough':
          replacement = `~~${selectedText || 'strikethrough text'}~~`;
          cursorOffsetStart = selectedText ? 0 : 2;
          cursorOffsetEnd = selectedText ? 0 : 2;
          break;
        case 'h1':
          replacement = `\n# ${selectedText || 'Heading 1'}\n`;
          cursorOffsetStart = selectedText ? 0 : 3;
          cursorOffsetEnd = selectedText ? 0 : 1;
          break;
        case 'h2':
          replacement = `\n## ${selectedText || 'Heading 2'}\n`;
          cursorOffsetStart = selectedText ? 0 : 4;
          cursorOffsetEnd = selectedText ? 0 : 1;
          break;
        case 'h3':
          replacement = `\n### ${selectedText || 'Heading 3'}\n`;
          cursorOffsetStart = selectedText ? 0 : 5;
          cursorOffsetEnd = selectedText ? 0 : 1;
          break;
        case 'quote':
          replacement = `\n> ${selectedText || 'Blockquote'}\n`;
          cursorOffsetStart = selectedText ? 0 : 3;
          cursorOffsetEnd = selectedText ? 0 : 1;
          break;
        case 'code':
          replacement = `\`${selectedText || 'code'}\``;
          cursorOffsetStart = selectedText ? 0 : 1;
          cursorOffsetEnd = selectedText ? 0 : 1;
          break;
        case 'codeblock':
          replacement = `\n\`\`\`javascript\n${selectedText || '// code here'}\n\`\`\`\n`;
          cursorOffsetStart = selectedText ? 0 : 15;
          cursorOffsetEnd = selectedText ? 0 : 5;
          break;
        case 'list':
          replacement = `\n- ${selectedText || 'List item'}`;
          cursorOffsetStart = selectedText ? 0 : 2;
          break;
        case 'orderedlist':
          replacement = `\n1. ${selectedText || 'List item'}`;
          cursorOffsetStart = selectedText ? 0 : 3;
          break;
        case 'link':
          replacement = `[${selectedText || 'link text'}](https://example.com)`;
          cursorOffsetStart = selectedText ? 0 : 1;
          cursorOffsetEnd = selectedText ? 0 : 21;
          break;
        case 'image':
          replacement = `![${selectedText || 'image alt'}](https://example.com/image.jpg)`;
          cursorOffsetStart = selectedText ? 0 : 2;
          cursorOffsetEnd = selectedText ? 0 : 49;
          break;
        case 'table':
          replacement = `\n| Header 1 | Header 2 |\n| -------- | -------- |\n| Cell 1   | Cell 2   |\n`;
          break;
        case 'hr':
          replacement = `\n\n---\n\n`;
          break;
      }

      const newValue = text.substring(0, start) + replacement + text.substring(end);

      if (onChange) {
        const event = {
          target: {
            ...textarea,
            value: newValue,
            name: props.name ?? '',
          },
        } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
        onChange(event);
      }

      setTimeout(() => {
        textarea.focus();
        if (selectedText) {
          textarea.setSelectionRange(start, start + replacement.length);
        } else {
          const newStart = start + cursorOffsetStart;
          const newEnd = start + replacement.length - cursorOffsetEnd;
          textarea.setSelectionRange(newStart, newEnd);
        }
      }, MICROTASK_MS);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (props.onKeyDown) props.onKeyDown(e);

      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      if (modifier) {
        switch (e.key.toLowerCase()) {
          case 'b':
            e.preventDefault();
            handleFormat('bold');
            break;
          case 'i':
            e.preventDefault();
            handleFormat('italic');
            break;
          case 'k':
            e.preventDefault();
            handleFormat('link');
            break;
        }
      }
    };

    const textValue = String(value ?? '');

    const toolbarButtons = (
      <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-100 bg-gray-50/70 px-3 py-1.5 shrink-0">
        <button
          type="button"
          onClick={() => {
            handleFormat('bold');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          title="Bold (Ctrl+B)"
        >
          <Bold size={15} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={() => {
            handleFormat('italic');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          title="Italic (Ctrl+I)"
        >
          <Italic size={15} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={() => {
            handleFormat('strikethrough');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          title="Strikethrough"
        >
          <Strikethrough size={15} strokeWidth={2.5} />
        </button>

        <div className="h-4 w-px bg-gray-200 mx-1.5" />

        <button
          type="button"
          onClick={() => {
            handleFormat('h1');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors font-bold text-xs"
          title="Heading 1"
        >
          <Heading1 size={15} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={() => {
            handleFormat('h2');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors font-bold text-xs"
          title="Heading 2"
        >
          <Heading2 size={15} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={() => {
            handleFormat('h3');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors font-bold text-xs"
          title="Heading 3"
        >
          <Heading3 size={15} strokeWidth={2.5} />
        </button>

        <div className="h-4 w-px bg-gray-200 mx-1.5" />

        <button
          type="button"
          onClick={() => {
            handleFormat('list');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          title="Bullet List"
        >
          <List size={15} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={() => {
            handleFormat('orderedlist');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          title="Numbered List"
        >
          <ListOrdered size={15} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={() => {
            handleFormat('quote');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          title="Blockquote"
        >
          <Quote size={15} strokeWidth={2.5} />
        </button>

        <div className="h-4 w-px bg-gray-200 mx-1.5" />

        <button
          type="button"
          onClick={() => {
            handleFormat('code');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          title="Inline Code"
        >
          <Code size={15} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={() => {
            handleFormat('codeblock');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          title="Code Block"
        >
          <Code2 size={15} strokeWidth={2.5} />
        </button>

        <div className="h-4 w-px bg-gray-200 mx-1.5" />

        <button
          type="button"
          onClick={() => {
            handleFormat('link');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          title="Link (Ctrl+K)"
        >
          <Link size={15} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={() => {
            handleFormat('image');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          title="Image"
        >
          <Image size={15} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={() => {
            handleFormat('table');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          title="Table"
        >
          <Table size={15} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={() => {
            handleFormat('hr');
          }}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          title="Horizontal Rule"
        >
          <Minus size={15} strokeWidth={2.5} />
        </button>
      </div>
    );

    const editorHeader = (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50/50 shrink-0">
        <div className="flex items-center gap-2">
          {Icon && (
            <div className="text-gray-400 group-focus-within:text-lime-900">
              <Icon size={16} strokeWidth={2.5} />
            </div>
          )}
          {label && (
            <span className="nav-label text-gray-900 tracking-tight flex items-center gap-1.5">
              {label}
              {isFullScreen && (
                <span className="text-xs font-semibold text-lime-600 bg-lime-50 border border-lime-100 rounded-md px-1.5 py-0.5">
                  Full Screen
                </span>
              )}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between sm:justify-end gap-2.5">
          <div className="flex rounded-lg bg-gray-100 p-0.5 border border-gray-200">
            <button
              type="button"
              onClick={() => {
                setActiveTab('write');
              }}
              className={`rounded-md px-3 py-1 text-xs font-bold transition-all flex items-center gap-1 ${
                activeTab === 'write' ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <PenTool size={12} />
              Write
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('preview');
              }}
              className={`rounded-md px-3 py-1 text-xs font-bold transition-all flex items-center gap-1 ${
                activeTab === 'preview' ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <Eye size={12} />
              Preview
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('split');
              }}
              className={`hidden md:flex rounded-md px-3 py-1 text-xs font-bold transition-all items-center gap-1 ${
                activeTab === 'split' ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <Columns size={12} />
              Split
            </button>
          </div>

          <div className="flex items-center gap-1.5">
            {isFullScreen && (
              <button
                type="button"
                onClick={() => {
                  setIsFullScreen(false);
                }}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-xs font-bold text-red-600 hover:bg-red-100 hover:text-red-700 transition-all shadow-sm cursor-pointer"
              >
                Close Fullscreen
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                setIsFullScreen(!isFullScreen);
              }}
              className="rounded-lg border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors shadow-sm cursor-pointer"
              title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
            >
              {isFullScreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </div>
        </div>
      </div>
    );

    const editorWorkspace = (
      <div className="flex flex-col flex-grow h-full min-h-0 bg-white">
        {activeTab !== 'preview' && toolbarButtons}

        <div
          className={`relative flex-1 min-h-0 ${activeTab === 'split' ? 'grid grid-cols-2 divide-x divide-gray-200 h-full' : ''}`}
        >
          {/* Write panel */}
          {(activeTab === 'write' || activeTab === 'split') && (
            <textarea
              ref={localRef}
              className={`
                w-full bg-white text-sm text-gray-900 placeholder:text-gray-400
                outline-none transition-all duration-200 border-none resize-none h-full
                pl-4 pr-4 py-3.5
                disabled:opacity-50 disabled:cursor-not-allowed
                ${className}
              `}
              disabled={isDisabled}
              value={value}
              onChange={onChange}
              onKeyDown={handleKeyDown}
              {...props}
            />
          )}

          {/* Preview panel */}
          {(activeTab === 'preview' || activeTab === 'split') && (
            <div
              className={`p-4 overflow-y-auto bg-gray-50/30 ${activeTab === 'split' ? 'max-h-full' : 'max-h-[500px]'}`}
            >
              {textValue ? (
                <Markdown content={textValue} />
              ) : (
                <p className="text-sm text-gray-400 italic">Nothing to preview</p>
              )}
            </div>
          )}
        </div>
      </div>
    );

    const containerStyle = isFullScreen
      ? 'rounded-2xl fixed inset-0 z-50 flex flex-col bg-white p-2 duration-200'
      : `rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm focus-within:border-lime-900 focus-within:ring-4 focus-within:ring-lime-900/5 transition-all ${containerClassName}`;

    return (
      <div className={isFullScreen ? 'h-[90vh] w-[100vw]' : 'space-y-2'}>
        <div className={containerStyle}>
          {/* Unified Editor Header (always visible & contains all views + toggles) */}
          {editorHeader}

          {/* Workspace Area */}
          <div className="flex-1 flex flex-col w-full min-h-0">{editorWorkspace}</div>
        </div>

        {!isFullScreen && error && (
          <p className="px-1 text-xs font-bold text-red-600 animate-in fade-in slide-in-from-top-1">{error}</p>
        )}
        {!isFullScreen && hint && !error && <p className="px-1 desc-sm italic leading-relaxed">{hint}</p>}
      </div>
    );
  }
);

MarkdownEditor.displayName = 'MarkdownEditor';
